import { useState } from 'react';
import { Dialog, DialogContent, Grid, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import { Button } from '../reusable-components';
import dbet_questionmark from '../images/dbet_questionmark.png';

interface VoidConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    couponId: string;
}

export default function VoidConfirmDialog({ open, onClose, onConfirm, couponId }: VoidConfirmDialogProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [confirmationState, setConfirmationState] = useState<'confirm' | 'success' | 'error'>('confirm');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const phrases = {
        confirmVoid: 'Vill du voida kupongen?',
        voidButton: 'Void',
        voidDone: 'Din kupong har voidats',
        unableToVoid: 'Kunde inte voida kupongen',
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm();
            setConfirmationState('success');
        } catch (error) {
            console.error('Void error:', error);
            setErrorMessage(phrases.unableToVoid);
            setConfirmationState('error');
        }
        setLoading(false);
    }

    const handleClose = () => {
        setConfirmationState('confirm');
        setErrorMessage('');
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    background: 'var(--background_1)',
                    width: '400px',
                }
            }}
        >
            <Grid container sx={{
                height: '50px',
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '8px'
            }}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: 'var(--text-black)',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Grid>

            <DialogContent sx={{ padding: '20px', overflow: 'auto', maxHeight: 'calc(90vh - 50px)' }}>
                <Grid container direction="column" spacing={2} alignItems="center">
                    {/* Icon */}
                    <Grid item>
                        <Box
                            component="img"
                            src={dbet_questionmark}
                            alt="Confirmation"
                            sx={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'contain'
                            }}
                        />
                    </Grid>

                    {/* Message */}
                    <Grid item xs={12}>
                        {confirmationState === 'confirm' && (
                            <>
                                <Typography sx={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: 'var(--text-black)',
                                    textAlign: 'center',
                                    marginBottom: '10px'
                                }}>
                                    {phrases.confirmVoid}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#4075FF',
                                    textAlign: 'center'
                                }}>
                                    {t('id')}: {couponId}
                                </Typography>
                            </>
                        )}

                        {confirmationState === 'success' && (
                            <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: 'var(--text-black)',
                                textAlign: 'center'
                            }}>
                                {phrases.voidDone}
                            </Typography>
                        )}

                        {confirmationState === 'error' && (
                            <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#FF0000',
                                textAlign: 'center'
                            }}>
                                {errorMessage || phrases.unableToVoid}
                            </Typography>
                        )}
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12} sx={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                        {confirmationState === 'confirm' ? (
                            <>
                                <Button
                                    style={{ minWidth: '120px' }}
                                    color="#FFFFFF"
                                    hoverColor="#FFFFFF"
                                    textColor="#4075FF"
                                    borderRadius="52px"
                                    onClick={handleClose}
                                    disabled={loading}
                                    border="2px solid #4075FF"
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    style={{ minWidth: '120px' }}
                                    color="#4075FF"
                                    hoverColor="#4075FF"
                                    borderRadius="52px"
                                    onClick={handleConfirm}
                                    disabled={loading}
                                >
                                    {phrases.voidButton}
                                </Button>
                            </>
                        ) : (
                            <Button
                                style={{ minWidth: '120px' }}
                                color="#4075FF"
                                hoverColor="#4075FF"
                                borderRadius="52px"
                                onClick={handleClose}
                            >
                                {t('ok')}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
